import { RefObject, useEffect } from 'react';
import { Chart as ChartJS } from 'chart.js';

type NavigationMode = 'index' | 'segment';

export interface UseChartKeyboardNavigationOptions {
  pointCount: number;
  datasetCount?: number;
  navigationMode?: NavigationMode;
  enabled?: boolean;
}

function getActiveElements(
  chart: ChartJS,
  index: number,
  datasetCount: number,
  navigationMode: NavigationMode
) {
  if (navigationMode === 'segment') {
    return [{ datasetIndex: 0, index }];
  }

  const count = Math.min(datasetCount, chart.data.datasets.length);
  return Array.from({ length: count }, (_, datasetIndex) => ({
    datasetIndex,
    index,
  }));
}

function getTooltipPosition(chart: ChartJS, index: number, datasetIndex = 0) {
  const meta = chart.getDatasetMeta(datasetIndex);
  const element = meta.data[index];
  if (!element || typeof element.x !== 'number' || typeof element.y !== 'number') {
    return { x: 0, y: 0 };
  }

  return { x: element.x, y: element.y };
}

function showTooltipAtIndex(
  chart: ChartJS,
  index: number,
  datasetCount: number,
  navigationMode: NavigationMode
) {
  const activeElements = getActiveElements(chart, index, datasetCount, navigationMode);
  chart.setActiveElements(activeElements);

  const tooltipElements = activeElements.map(({ datasetIndex, index: pointIndex }) => ({
    datasetIndex,
    index: pointIndex,
  }));
  const position = getTooltipPosition(chart, index, activeElements[0]?.datasetIndex ?? 0);

  chart.tooltip?.setActiveElements(tooltipElements, position);
  chart.update('none');
}

function hideTooltip(chart: ChartJS) {
  chart.setActiveElements([]);
  chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
  chart.update('none');
}

export function useChartKeyboardNavigation(
  chartRef: RefObject<ChartJS | null>,
  {
    pointCount,
    datasetCount = 1,
    navigationMode = 'index',
    enabled = true,
  }: UseChartKeyboardNavigationOptions,
  chartReady = false
) {
  useEffect(() => {
    if (!enabled || !chartReady || pointCount <= 0) return;

    const chart = chartRef.current;
    if (!chart) return;

    const canvas = chart.canvas;
    canvas.tabIndex = 0;

    let activeIndex = 0;

    const handleFocus = () => {
      activeIndex = 0;
      showTooltipAtIndex(chart, activeIndex, datasetCount, navigationMode);
    };

    const handleBlur = () => {
      hideTooltip(chart);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideTooltip(chart);
        event.preventDefault();
        return;
      }

      const previousKeys = ['ArrowLeft', 'ArrowUp'];
      const nextKeys = ['ArrowRight', 'ArrowDown'];

      if (previousKeys.includes(event.key)) {
        activeIndex = Math.max(0, activeIndex - 1);
        showTooltipAtIndex(chart, activeIndex, datasetCount, navigationMode);
        event.preventDefault();
        return;
      }

      if (nextKeys.includes(event.key)) {
        activeIndex = Math.min(pointCount - 1, activeIndex + 1);
        showTooltipAtIndex(chart, activeIndex, datasetCount, navigationMode);
        event.preventDefault();
        return;
      }

      if (event.key === 'Home') {
        activeIndex = 0;
        showTooltipAtIndex(chart, activeIndex, datasetCount, navigationMode);
        event.preventDefault();
        return;
      }

      if (event.key === 'End') {
        activeIndex = pointCount - 1;
        showTooltipAtIndex(chart, activeIndex, datasetCount, navigationMode);
        event.preventDefault();
      }
    };

    canvas.addEventListener('focus', handleFocus);
    canvas.addEventListener('blur', handleBlur);
    canvas.addEventListener('keydown', handleKeyDown);

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideTooltip(chart);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      canvas.removeEventListener('focus', handleFocus);
      canvas.removeEventListener('blur', handleBlur);
      canvas.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [chartRef, chartReady, datasetCount, enabled, navigationMode, pointCount]);
}
